import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Company } from 'shared/types';
import { useAuth } from 'shared/contexts/AuthContext';
import { isCompanyRole } from 'shared/utils/roleUtils';
import { useCompany } from './queries/useCompanies';

interface CompanyContextResult {
  companyId: string | undefined;
  urlCompanyId: string | undefined; // Company ID from URL params only
  company: Company | null;
  isCompanyContext: boolean;
  isGlobalContext: boolean;
  loading: boolean;
  error: string | null;
}

/**
 * Custom hook to detect and load company context
 * Used across pages to determine if we're in a company-specific context
 *
 * Company context is determined by:
 * 1. URL params (e.g., /companies/:companyId/...)
 * 2. User's own company if they have a company-specific role (client_*, contratista_*)
 *
 * The company fetch is backed by the shared `useCompany` React Query hook
 * (5-min staleTime) so multiple components that read this context — Sidebar,
 * Layout, ModuleGuard, page-level consumers — share one in-flight request
 * and one cached copy per companyId.
 */
export const useCompanyContext = (): CompanyContextResult => {
  const { companyId: urlCompanyId } = useParams<{ companyId: string }>();
  const { user } = useAuth();

  // Determine company ID from URL or user's company for company-specific roles
  const userCompanyId = user?.company?._id;
  const isUserCompanyRole = user?.role ? isCompanyRole(user.role) : false;

  // Use URL companyId if available, otherwise use user's company if they have a company role
  const companyId = urlCompanyId || (isUserCompanyRole ? userCompanyId : undefined);

  const isCompanyContext = !!companyId && companyId !== 'unlinked-users';
  const isGlobalContext = !isCompanyContext;

  // When we already have the user's own company in AuthContext, prefer it for
  // their own company id — avoids a redundant API hit on initial mount.
  const canUseAuthCompany = !urlCompanyId && isUserCompanyRole && !!user?.company;

  const query = useCompany(
    companyId || '',
    isCompanyContext && !canUseAuthCompany,
  );

  const company = useMemo<Company | null>(() => {
    if (!isCompanyContext) return null;
    if (canUseAuthCompany) return (user?.company as Company) || null;
    return (query.data?.data as Company) || null;
  }, [isCompanyContext, canUseAuthCompany, user?.company, query.data]);

  const error = useMemo<string | null>(() => {
    if (!isCompanyContext || canUseAuthCompany) return null;
    if (query.error) return 'Error al cargar la empresa';
    if (query.data && !query.data.success) return 'No se pudo cargar la empresa';
    return null;
  }, [isCompanyContext, canUseAuthCompany, query.error, query.data]);

  return {
    companyId,
    urlCompanyId,
    company,
    isCompanyContext,
    isGlobalContext,
    loading: !canUseAuthCompany && isCompanyContext && query.isLoading,
    error,
  };
};
